import React, { useCallback, useEffect, useState } from "react";
import { LogOut, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { clearToken } from "@/lib/tokenStore";
import CguScrollAccept from "@/components/price/CguScrollAccept";
import { DeleteAccountDialog } from "@/components/price/PriceProfileSheet";

// Acceptation obligatoire des nouvelles CGU (comptes passager / conducteur).
export default function CguGate() {
  const { user, logout } = useAuth();
  const [version, setVersion] = useState(null);
  const [refused, setRefused] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { pathname } = useLocation();
  // Uniquement dans l'espace applicatif : jamais sur les pages publiques du site.
  const inApp = /^\/(dashboard|verify-identity|become-driver|activate)(\/|$)/.test(pathname);
  const eligible = user && user.role !== "admin";

  const check = useCallback(() => {
    if (!eligible) return setVersion(null);
    api.get("/cgu/me").then(({ data }) => setVersion(data.required ? data.current : null)).catch(() => {});
  }, [eligible]);

  useEffect(() => { check(); }, [check, user?.id]);
  useEffect(() => {
    window.addEventListener("price:cgu-required", check);
    return () => window.removeEventListener("price:cgu-required", check);
  }, [check]);

  if (!eligible || !version || !inApp) return null;

  const accept = async () => {
    try {
      await api.post("/cgu/accept", { version_id: version.id, method: "update", scrolled_to_end: true });
      toast.success("Merci, les CGU sont acceptées");
      setVersion(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const leave = async () => {
    try { await logout(); } catch (_) { await clearToken(); }
    setVersion(null);
    window.location.assign("/login");
  };

  return (
    <div className="fixed inset-0 z-[160] flex items-end justify-center bg-black/70 sm:items-center sm:p-4" data-testid="cgu-gate">
      <div className="flex max-h-[94vh] w-full max-w-2xl flex-col rounded-t-3xl bg-[#FFF8F1] street-border sm:rounded-3xl animate-in slide-in-from-bottom-6 duration-300">
        {refused
          ? <Refused onBack={() => setRefused(false)} onLogout={leave} onDelete={() => setDeleting(true)} />
          : <CguScrollAccept version={version} title="Nouvelles conditions d'utilisation" intro="merci de les lire jusqu'en bas puis de les accepter pour continuer à utiliser Price."
              onAccept={accept} onRefuse={() => { api.post("/cgu/refuse").catch(() => {}); setRefused(true); }} />}
      </div>
      {deleting && (
        <DeleteAccountDialog onClose={() => setDeleting(false)}
          onDeleted={async () => { await clearToken(); window.location.assign("/"); }} />
      )}
    </div>
  );
}

function Refused({ onBack, onLogout, onDelete }) {
  return (
    <div className="p-6 text-center" data-testid="cgu-gate-refused">
      <h2 className="font-[Outfit] text-[22px] font-black">Tu as refusé les nouvelles CGU</h2>
      <p className="mt-2 text-[14px] font-medium text-foreground/70">
        Sans acceptation, tu ne peux plus réserver, demander ni proposer de trajet sur Price. Les trajets et paiements déjà engagés restent régis par la version que tu avais acceptée.
        Tu peux à tout moment revenir accepter les CGU, te déconnecter ou supprimer ton compte. Une question ? contact@pricecovoit.fr
      </p>
      <button type="button" onClick={onBack} className="street-press mt-5 h-12 w-full rounded-full bg-primary font-[Outfit] font-black text-primary-foreground street-border street-shadow-sm" data-testid="cgu-gate-back">
        Relire et accepter
      </button>
      <button type="button" onClick={onLogout} className="street-press mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-white font-[Outfit] font-black street-border-thin" data-testid="cgu-gate-logout">
        <LogOut className="h-4 w-4" />Se déconnecter
      </button>
      <a href="/confidentialite" target="_blank" rel="noreferrer" className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-full bg-white text-[13px] font-black street-border-thin" data-testid="cgu-gate-privacy">
        <ShieldCheck className="h-4 w-4" />Mes données personnelles
      </a>
      <button type="button" onClick={onDelete} className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-full text-[13px] font-black text-red-600" data-testid="cgu-gate-delete">
        <Trash2 className="h-4 w-4" />Supprimer mon compte
      </button>
    </div>
  );
}
